import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, InputAPI, PanelAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnBlur, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AbortableSubmitEventArgs } from '@philips-internal/oswald-ui-core/lib/component/types/submittable';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { AccountCreationConfirmationSmsCodeAliases, FragmentNames } from '../../codegen';
import { HttpStatus } from '../../constants/HttpStatus';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { cast } from '../../utils/cast-helpers';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';

interface SmsInfoForValidationProps extends Record<string, Value> {
  username: string;
}

interface ResendSmsCodeProps extends Record<string, Value> {
  username: string;
}

@Controller({ component: AccountCreationConfirmationSmsCodeAliases.MODAL_SEND_SMS_CODE,
  fragment: FragmentNames.ACCOUNT_CREATION_CONFIRMATION_SMS_CODE })
class AccountConfirmationSmsCodeController extends BaseController {

  @Inject(AccountCreationConfirmationSmsCodeAliases.INPUT_SMS_CODE)
    inputSmsCode!: InputAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.SEND_SMS_CODE_FIELD)
    fieldSmsCode!: FieldAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.PANEL_LOADER_SEND_SMS_CODE)
    panelLoader!: PanelAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.PANEL_TEXT_INFORMATION_RESEND_SMS)
    panelInformationResendSmsCode!: PanelAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.TEXT_INFORMATION_RESEND_SMS_CODE)
    textInformationResendSmsCode!: TextAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.BUTTON_RESEND_SMS_CODE)
    btnResendSmsCode!: ButtonAPI;

  @Inject(AccountCreationConfirmationSmsCodeAliases.BUTTON_RESEND_SMS_CODE_ACTION)
    btnResendSmsCodeAction!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  private intervalsToClear: Map<string, NodeJS.Timeout> = new Map();

  @OnClick({ target: [AccountCreationConfirmationSmsCodeAliases.BUTTON_RESEND_SMS_CODE,
    AccountCreationConfirmationSmsCodeAliases.BUTTON_RESEND_SMS_CODE_ACTION] })
  onResendSmsCodeClick(): void {
    this.panelLoader.setVisible( true );
    const applicationContext = getFoundationDataContext();
    const dsLogin = applicationContext.get('dsLogin');

    const body: ResendSmsCodeProps = {
      username: dsLogin,
    }

    HttpService.post(APPLICATION_ENDPOINTS.RESEND_SMS_CODE, {}, { body })
      .then(()=>this.handleSuccessResendCode())
      .catch(() => this.panelLoader.setVisible(false));
  }

  handleSuccessResendCode(): void{
    this.panelLoader.setVisible( false );
    if( !isMobile() ){
      this.btnResendSmsCode.setEnabled(false);
    }else{
      this.btnResendSmsCodeAction.setEnabled(false);
    }
    this.setCounter();
  }

  handleSuccessConfirmationCode( ): boolean {
    this.panelLoader.setVisible( false );
    const applicationContext = getFoundationDataContext();
    applicationContext.set( { 'phoneNumber' : null } );
    this.submittableFragmentContext.submit(new SubmitEvent());

    return false;
  }

  async handleErrorValidation({ response }: HttpServiceError<string>): Promise<boolean> {
    this.panelLoader.setVisible( false );
    if(response?.status === HttpStatus.UNPROCESSABLE_ENTITY){
      this.fieldSmsCode.addLocalValidator('invalid-sms-code-local-validator', async () => {
        return [{
          message: MessageService.translate('patient.incorrect.key'),
          name: 'patient.incorrect.key',
          valid: false
        }];
      });
      await this.fieldSmsCode.validate();
    } else {
      showToastNotificationError(MessageService.translate('newaccount.error.internal.server.error.try.again'));
    }

    return Promise.resolve(true);
  }

  setCounter():void{
    let counter = 0;
    const countInterval = setInterval(() => {
      this.panelInformationResendSmsCode.setVisible(true);
      counter++;
      if(counter < 60){
        const msgSucessfull =  MessageService.translate('sms.resend.successful');
        const message = MessageService.translate('account.confirmation.sms.wait.seconds');
        const formattedMessage = message.replace('#@seg#@', String((60 - counter )));
        this.textInformationResendSmsCode.setValue(msgSucessfull.concat('\n').concat(formattedMessage ));
      } else {
        clearInterval(countInterval);
        if (!isMobile()) {
          if(this.btnResendSmsCode && this.textInformationResendSmsCode){
            this.btnResendSmsCode.setEnabled(true);
            this.textInformationResendSmsCode.setValue(MessageService.translate('send.new.code.sms.available'));
          }
        }else{
          if(this.btnResendSmsCodeAction && this.textInformationResendSmsCode){
            this.btnResendSmsCodeAction.setEnabled(true);
            this.textInformationResendSmsCode.setValue(MessageService.translate('send.new.code.sms.available'));
          }
        }
      }
    }, 1000);

    this.intervalsToClear.set('sms-counter', countInterval);
  }

  @OnUnmount('modal-send-sms-code')
  handleModalSendSmsCodeLoading(): void {
    for (const interval of this.intervalsToClear.values()) {
      if(interval) {
        clearInterval(interval);
      }
    }
  }

  @OnClick({ target: [ AccountCreationConfirmationSmsCodeAliases.BUTTON_CONFIRM,
    AccountCreationConfirmationSmsCodeAliases.BUTTON_CONFIRM_ACTION] })
  onConfirmSmsCodeClick(): void  {
    this.panelLoader.setVisible( true );

    const code = this.inputSmsCode.getValue();
    const applicationContext = getFoundationDataContext();
    const dsLogin = applicationContext.get('dsLogin');

    const body: SmsInfoForValidationProps = {
      username: dsLogin,
    }

    HttpService.put(APPLICATION_ENDPOINTS.CONFIRM_SMS_CODE, { smsCode: code }, { body })
      .then(() => {
        this.handleSuccessConfirmationCode();
      })
      .catch((error) => {
        this.handleErrorValidation(error);
      });
  }

  @OnMount({ target: AccountCreationConfirmationSmsCodeAliases.PANEL_LOADER_SEND_SMS_CODE})
  onMountPanelLoader( ): void {
    this.panelLoader.setVisible( false );
  }

  @OnMount({ target: AccountCreationConfirmationSmsCodeAliases.PANEL_TEXT_INFORMATION_RESEND_SMS })
  onMountPanelTextInformation( ): void {
    this.panelInformationResendSmsCode.setVisible( false );
  }

  @OnMount({ target: AccountCreationConfirmationSmsCodeAliases.TEXT_INFORMATION_SMS_CHECK})
  onMountPanelTextInformationPrimary( evt: MountEvent ): void {
    const target: TextAPI = cast(evt.target);
    const applicationContext = getFoundationDataContext();
    let phoneNumber = applicationContext.get('phoneNumber') as string;
    const message = MessageService.translate('patient.check.phone');

    if (!phoneNumber.includes('*')) {
      phoneNumber = phoneNumber.replaceAll(/\d(?=(?:\D*\d){4})/g, '*');
    }
    const formattedMessage = message + ' ' + phoneNumber;
    target.setValue(formattedMessage);

  }
  @OnMount({target: AccountCreationConfirmationSmsCodeAliases.PANEL_ACTIONS})
  onMountPanelActions(evt: MountEvent):void{
    const target: PanelAPI = cast(evt.target);
    if( isMobile() ){
      target.setVisible(false);
    }
  }

  @OnMount({target: AccountCreationConfirmationSmsCodeAliases.PANEL_BTNS_ACTIONS})
  onMountPanelGlobalActions(evt: MountEvent):void{
    const target: PanelAPI = cast(evt.target);
    if( !isMobile() ){
      target.setVisible(false);
    }
  }

  @OnBlur(AccountCreationConfirmationSmsCodeAliases.SEND_SMS_CODE_FIELD)
  handleSendSmsCodeFieldOutFocus(event: FocusEvent): void {
    const fieldAPI = event.target as unknown as FieldAPI;
    fieldAPI.removeLocalValidator('invalid-sms-code-local-validator');

    setTimeout(async () => {
      await fieldAPI.validate();
    })
  }

  @OnBeforeSubmit(AccountCreationConfirmationSmsCodeAliases.FORM_SEND_SMS_CODE)
  handleFormSubmit(event: AbortableSubmitEventArgs): void {
    const { abort } = event;

    this.panelLoader.setVisible(true);

    const code = this.inputSmsCode.getValue();
    const applicationContext = getFoundationDataContext();
    const dsLogin = applicationContext.get('dsLogin');

    const body: SmsInfoForValidationProps = {
      username: dsLogin,
    }

    const promise = PatientHttpService.put(APPLICATION_ENDPOINTS.CONFIRM_SMS_CODE, { smsCode: code }, { body })
      .then(() =>this.handleSuccessConfirmationCode())
      .catch((error: HttpServiceError<string>) => this.handleErrorValidation(error));

    abort(promise);
  }
}

export default AccountConfirmationSmsCodeController;